import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Button } from '@blade/ui-kit'
import { PremiumCurrencyInfo, premiumFontCss } from '@root/entities/premium/ui'
import { reatomCurrencyCheck } from '@root/features/premium/check-currency/atoms/reatom-currency-check'
import { CheckCurrencyAlert } from '@root/features/premium/check-currency/ui/desktop'
import { Separator } from '@root/shared/ui-kit'

import type { CurrencyModel } from '@root/entities/currency/model'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  currency: CurrencyModel
  onSuccess: () => void
} & ClassNameProps

export const BuyPremiumSafetyCheck: React.FC<Props> = ({
  className,
  currency,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const onSuccessRef = useLatest(onSuccess)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomCurrencyCheck({
        currency: currency.address,
        network: currency.network,
      }),
    [currency],
  )

  const [checkState] = useAtom(model)
  const checkSafety = useAction(model.execute)

  useEffect(() => {
    if (checkState.value?.hasPassedSecurityChecks) {
      onSuccessRef.current()
    }
  }, [checkState.value, onSuccessRef])

  return (
    <Root className={className}>
      <Description>
        <Trans
          t={t}
          i18nKey="premium.pay.safety-check.description"
          components={[<span key={0} />]}
        />
      </Description>
      <PremiumCurrencyInfo currency={currency} premium={undefined} />
      {checkState.value ? (
        <CheckCurrencyAlert
          css={css`
            margin-inline: -30px;
            margin-block: 10px;
          `}
          result={checkState.value}
        />
      ) : undefined}
      <Separator
        variant="gradient"
        css={css`
          margin-block: 20px;
        `}
      />
      <Button
        fullWidth
        variant="solid"
        size="s"
        radius="rounded"
        color="primary"
        busy={checkState.loading}
        onClick={() => checkSafety()}
      >
        {t('premium.pay.safety-check.check-btn')}
      </Button>
    </Root>
  )
}

const Root = styled.div`
  padding-block: 20px 22px;
  padding-inline: 30px;
`

const Description = styled.div`
  color: var(--text-9);
  text-align: center;

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 14px;

  & span {
    ${premiumFontCss}
  }
`
